import GoogleIdentityCard from './GoogleIdentityCard';
import ExternalIdentityCard from './ExternalIdentityCard';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import {
  recordCurrentDevice,
  getCurrentFingerprint,
  formatRelativeTime,
  type TrackedDevice,
} from '@/lib/deviceTracking';

import { QRCodeCanvas } from 'qrcode.react';

interface TwoFaSetupState {
  setup_id: string;
  secret_once: string;
  otpauth_uri: string;
  expires_in: number;
}

export default function SecurityPage() {
  const navigate = useNavigate();
  const { user, setup2Fa, enable2Fa, disable2Fa } = useAuth();

  /* 2FA state */
  const twoFAEnabled = user?.two_factor_status === 'on';
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [verifyDigits, setVerifyDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupData, setSetupData] = useState<TwoFaSetupState | null>(null);
  const [toast, setToast] = useState('');

  /* Refs for digit inputs */
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);

  /* Device state */
  const [devices, setDevices] = useState<TrackedDevice[]>([]);
  const [currentFingerprint, setCurrentFingerprint] = useState('');

  useEffect(() => {
    const all = recordCurrentDevice();
    setDevices(all);
    setCurrentFingerprint(getCurrentFingerprint());
  }, []);

  /* Auto-focus first digit when a modal opens */
  useEffect(() => {
    if (showSetup || showDisable) {
      setTimeout(() => digitRefs.current[0]?.focus(), 150);
    }
  }, [showSetup, showDisable]);

  /* Auto-dismiss toast */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  /* ── Verification helpers ── */
  const verifyCode = verifyDigits.join('');
  const verifyFull = verifyCode.length === 6;

  const handleDigitInput = (idx: number, val: string) => {
    setVerifyError('');
    if (val.length > 1) {
      const digits = val.replace(/\D/g, '').slice(0, 6).split('');
      const newDigits = ['', '', '', '', '', ''];
      digits.forEach((d, i) => { newDigits[i] = d; });
      setVerifyDigits(newDigits);
      const nextIdx = Math.min(digits.length, 5);
      digitRefs.current[nextIdx]?.focus();
      return;
    }
    const digit = val.replace(/\D/g, '').slice(-1);
    const newDigits = [...verifyDigits];
    newDigits[idx] = digit;
    setVerifyDigits(newDigits);
    if (digit && idx < 5) {
      digitRefs.current[idx + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    setVerifyError('');
    if (e.key === 'Backspace') {
      if (!verifyDigits[idx] && idx > 0) {
        const newDigits = [...verifyDigits];
        newDigits[idx - 1] = '';
        setVerifyDigits(newDigits);
        digitRefs.current[idx - 1]?.focus();
      } else {
        const newDigits = [...verifyDigits];
        newDigits[idx] = '';
        setVerifyDigits(newDigits);
      }
    }
  };

  const resetVerify = () => {
    setVerifyDigits(['', '', '', '', '', '']);
    setVerifyError('');
    setVerifying(false);
  };

  const restoreViewportAfterSheetClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 0);
  };

  const closeSetup = () => {
    restoreViewportAfterSheetClose();
    setShowSetup(false);
    setSetupData(null);
    setSetupLoading(false);
    resetVerify();
  };

  const closeDisable = () => {
    restoreViewportAfterSheetClose();
    setShowDisable(false);
    resetVerify();
  };

  /* ── Enable 2FA ── */
  const handleEnableConfirm = async () => {
    if (!verifyFull || !setupData) return;
    setVerifying(true);
    try {
      await enable2Fa(setupData.setup_id, verifyCode);
      closeSetup();
      setToast('2FA 已开启');
    } catch (err) {
      setVerifyError(err instanceof ApiError ? err.message : '验证码错误，请输入正确的 6 位验证码');
    } finally {
      setVerifying(false);
    }
  };

  /* ── Disable 2FA ── */
  const handleDisableConfirm = async () => {
    if (!verifyFull) return;
    setVerifying(true);
    try {
      await disable2Fa(verifyCode);
      closeDisable();
      setToast('2FA 已关闭');
    } catch (err) {
      setVerifyError(err instanceof ApiError ? err.message : '验证码错误，请输入正确的 6 位验证码');
    } finally {
      setVerifying(false);
    }
  };

  const openSetup = async () => {
    resetVerify();
    setSetupLoading(true);
    setShowSetup(true);
    try {
      const result = await setup2Fa();
      setSetupData(result);
    } catch (err) {
      setVerifyError(err instanceof ApiError ? err.message : '无法创建 2FA 设置，请重新登录后再试');
    } finally {
      setSetupLoading(false);
    }
  };
  const openDisable = () => { resetVerify(); setShowDisable(true); };

  /* ── Shared digit input renderer ── */
  const renderDigitInputs = (filledColor: 'indigo' | 'red') => {
    const bgClass = filledColor === 'indigo' ? 'bg-indigo-50' : 'bg-red-50';
    const borderClass = filledColor === 'indigo' ? 'border-indigo-500' : 'border-red-500';
    const textClass = filledColor === 'indigo' ? 'text-indigo-600' : 'text-red-600';
    const caretColor = filledColor === 'indigo' ? '#6366F1' : '#EF4444';

    return (
      <div className="flex items-center gap-2 mb-4">
        {verifyDigits.map((d, idx) => {
          const isFilled = Boolean(d);
          return (
            <input
              key={idx}
              ref={el => { digitRefs.current[idx] = el; }}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={d}
              disabled={verifying}
              onChange={e => handleDigitInput(idx, e.target.value)}
              onKeyDown={e => handleDigitKeyDown(idx, e)}
              onFocus={e => e.target.select()}
              onPaste={e => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                const newDigits = ['', '', '', '', '', ''];
                pasted.split('').forEach((dd, i) => { newDigits[i] = dd; });
                setVerifyDigits(newDigits);
                setVerifyError('');
                const nextIdx = Math.min(pasted.length, 5);
                digitRefs.current[nextIdx]?.focus();
              }}
              className={`flex-1 min-w-0 text-center font-semibold rounded-xl transition-all ${isFilled ? bgClass : ''}`}
              style={{
                height: '48px',
                fontSize: '20px',
                background: isFilled ? undefined : '#FAFAFB',
                border: `1.5px solid ${isFilled ? undefined : '#E5E7EB'}`,
                borderColor: isFilled ? undefined : '#E5E7EB',
                color: isFilled ? undefined : '#111827',
                outline: 'none',
                caretColor,
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Toast */}
      {toast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[70] bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-full shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center gap-3 px-5 h-12">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
            <i className="ri-arrow-left-s-line text-gray-600 text-lg" />
          </button>
          <h1 className="text-gray-900 font-bold text-base">安全中心</h1>
        </div>
      </div>

      <div className="px-5 pt-4 flex flex-col gap-3">
        <GoogleIdentityCard />
        <ExternalIdentityCard provider="apple" />
        <ExternalIdentityCard provider="x" />
        {/* ═══════ 2FA Card ═══════ */}
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-100">
                <i className="ri-smartphone-line text-indigo-500 text-base" />
              </div>
              <div>
                <h2 className="text-gray-900 font-bold text-sm">双重验证 (2FA)</h2>
                <span className={`text-xs font-medium ${twoFAEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {twoFAEnabled ? '已开启' : '未开启'}
                </span>
              </div>
            </div>
            {twoFAEnabled ? (
              <button
                onClick={openDisable}
                className="px-4 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-500 active:bg-red-100 transition-colors"
              >
                关闭 2FA
              </button>
            ) : (
              <button
                onClick={openSetup}
                className="px-4 py-1.5 rounded-full text-xs font-medium bg-indigo-500 text-white active:bg-indigo-600 transition-colors"
              >
                开启 2FA
              </button>
            )}
          </div>
          <p className="text-gray-400 text-xs">
            {twoFAEnabled
              ? '已启用 Authenticator App 验证，登录时需要输入动态验证码。'
              : '建议开启双重验证以保护账户安全，开启后登录需输入 Authenticator App 动态验证码。'}
          </p>
        </div>

        {/* ═══════ Device Card ═══════ */}
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <h2 className="text-gray-900 font-bold text-sm mb-3">当前设备</h2>
          {devices.length === 0 ? (
            <div className="flex flex-col items-center py-4 text-center">
              <i className="ri-smartphone-line text-gray-300 text-2xl mb-2" />
              <p className="text-gray-400 text-xs">暂无设备信息</p>
            </div>
          ) : (
            (() => {
              const current = devices.find(d => d.id === currentFingerprint) || devices[0];
              return (
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${current.icon} text-indigo-500 text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 text-xs font-semibold">{current.name}</span>
                      <span className="bg-green-50 text-green-600 text-[10px] px-1.5 py-0.5 rounded-full leading-tight">当前</span>
                    </div>
                    <p className="text-gray-400 text-[10px] mt-0.5 leading-tight">
                      {current.os} · {current.browser} · {formatRelativeTime(current.lastActive)}
                    </p>
                  </div>
                </div>
              );
            })()
          )}
          <p className="text-gray-300 text-[10px] mt-3 leading-relaxed">
            设备信息仅用于安全识别，暂不支持多设备管理。
          </p>
        </div>
      </div>

      {/* ═══════ Enable 2FA Modal ═══════ */}
      {showSetup && (
        <div className="fixed inset-0 z-[60] flex items-end" onClick={closeSetup}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-t-3xl p-5 pb-8 w-full max-h-[90vh] overflow-y-auto" style={{ maxWidth: '430px', margin: '0 auto' }} onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
            <h2 className="text-gray-900 font-bold text-lg mb-1">开启双重验证</h2>
            <p className="text-gray-400 text-xs mb-5">使用 Authenticator App 扫描二维码或手动输入密钥</p>

            {/* QR Code */}
            <div className="w-40 h-40 mx-auto mb-4 rounded-2xl overflow-hidden bg-white flex items-center justify-center" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              {setupLoading ? (
                <div className="w-8 h-8 border-2 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
              ) : setupData ? (
                <QRCodeCanvas
                  value={setupData.otpauth_uri}
                  size={140}
                  bgColor="#ffffff"
                  fgColor="#111827"
                  level="M"
                  includeMargin
                />
              ) : (
                <span className="text-gray-400 text-xs">生成失败</span>
              )}
            </div>

            {/* Secret key */}
            <div className="bg-gray-50 rounded-xl px-4 py-3 mb-5 flex items-center justify-between">
              <span className="text-gray-500 text-xs font-mono tracking-wider">
                {setupData?.secret_once || '等待生成密钥'}
              </span>
              <button
                onClick={() => {
                  if (!setupData?.secret_once) return;
                  navigator.clipboard.writeText(setupData.secret_once);
                  setToast('密钥已复制');
                }}
                className="text-indigo-500 text-xs font-medium active:text-indigo-700"
                disabled={!setupData?.secret_once}
              >
                复制
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-gray-300 text-xs">输入验证码确认</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* 6-digit input */}
            {renderDigitInputs('indigo')}

            {verifyError && (
              <p className="text-red-500 text-xs text-center mb-3">{verifyError}</p>
            )}

            <button
              onClick={handleEnableConfirm}
              disabled={!verifyFull || verifying || !setupData}
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all active:opacity-80"
              style={{
                background: verifyFull ? 'linear-gradient(135deg, #6366F1, #7C3AED)' : '#E5E7EB',
                color: verifyFull ? '#FFFFFF' : '#9CA3AF',
                cursor: verifyFull && !verifying ? 'pointer' : 'not-allowed',
              }}
            >
              {verifying ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  验证中...
                </span>
              ) : (
                '确认开启'
              )}
            </button>
          </div>
        </div>
      )}

      {/* ═══════ Disable 2FA Modal ═══════ */}
      {showDisable && (
        <div className="fixed inset-0 z-[60] flex items-end" onClick={closeDisable}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-t-3xl p-5 pb-8 w-full" style={{ maxWidth: '430px', margin: '0 auto' }} onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
            <h2 className="text-gray-900 font-bold text-lg mb-1">关闭双重验证</h2>
            <p className="text-gray-400 text-xs mb-5">关闭后登录将不再需要 Authenticator App 验证码，账户安全性将降低。</p>

            {/* 6-digit input */}
            <label className="text-gray-500 text-xs font-medium mb-2 block">输入 Authenticator App 中的 6 位验证码确认</label>
            {renderDigitInputs('red')}

            {verifyError && (
              <p className="text-red-500 text-xs text-center mb-3">{verifyError}</p>
            )}

            <button
              onClick={handleDisableConfirm}
              disabled={!verifyFull || verifying}
              className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all active:opacity-80"
              style={{
                background: verifyFull ? '#EF4444' : '#E5E7EB',
                color: verifyFull ? '#FFFFFF' : '#9CA3AF',
                cursor: verifyFull && !verifying ? 'pointer' : 'not-allowed',
              }}
            >
              {verifying ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  验证中...
                </span>
              ) : (
                '确认关闭'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
